// Shared TinyMCE config for every rich-text field whose content ends up
// inside an actual sent email (signature templates, legal disclaimers,
// banner campaigns) — see RichHtmlEditor.jsx, which pairs this with the
// plugin/skin imports it depends on. Originally lived only in
// SignatureForm.jsx; extracted so Disclaimers.jsx/Campaigns.jsx get the
// exact same authoring experience instead of a stripped-down one.

import { api, endpoints } from '../config/api';
import { pick } from '../i18n';

const HIDE_IF_EMPTY = {
  text: { tr: 'Boşsa Gizle', en: 'Hide if Empty' },
  tooltip: {
    tr: 'Seçimi, içindeki {{Alan}} boşsa e-postada tamamen gizlenecek şekilde işaretle',
    en: 'Mark the selection to be hidden entirely in the email when the {{Field}} inside it is empty',
  },
  warning: {
    tr: 'Önce içinde bir {{Alan}} (ör. {{Mobile}}) geçen bir bölüm seçin.',
    en: 'First select a part that contains a {{Field}} (e.g. {{Mobile}}).',
  },
};

// Email HTML is sent as-is into Outlook/Gmail/etc — those clients ignore
// the editor's own CSS entirely and fall back to their own defaults, which
// don't match ours (this was rendering with generous Poppins-styled
// paragraph spacing in the editor but cramped, different-font text in the
// actual email). Email HTML needs to carry its own look inline since
// nothing else travels with it, so every paragraph gets these inline
// styles baked in via forced_root_block_attrs — and content_style mirrors
// the same values so the editor preview matches what actually gets saved
// and sent, instead of just looking like the admin app.
export const EMAIL_PARAGRAPH_STYLE = 'margin: 0 0 4px 0; font-family: Arial, Helvetica, sans-serif; font-size: 14px; line-height: 1.4; color: #1a1a1a;';

// A small curated palette for forecolor/backcolor, appended after the org's
// own named brand colors (see buildEmailEditorConfig's brandColors param) —
// deliberately NOT an attempt to reproduce TinyMCE's own internal default
// color_map (that's an implementation detail with no stable, greppable
// source in the installed package to copy from reliably, and hand-guessing
// it risks silently drifting from whatever a future TinyMCE upgrade
// actually ships). A generic, review-stable set of neutrals + primaries so
// authors aren't left with only their brand colors.
const CURATED_COLORS = [
  ['000000', { tr: 'Siyah', en: 'Black' }],
  ['4B5563', { tr: 'Koyu Gri', en: 'Dark Gray' }],
  ['9CA3AF', { tr: 'Gri', en: 'Gray' }],
  ['FFFFFF', { tr: 'Beyaz', en: 'White' }],
  ['DC2626', { tr: 'Kırmızı', en: 'Red' }],
  ['F59E0B', { tr: 'Turuncu', en: 'Orange' }],
  ['10B981', { tr: 'Yeşil', en: 'Green' }],
  ['2563EB', { tr: 'Mavi', en: 'Blue' }],
];
const curatedColorMap = () => CURATED_COLORS.flatMap(([hex, name]) => [hex, pick(name)]);

const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

export function buildEmailEditorConfig({ height = 360, brandColors } = {}) {
  const validBrandColors = (brandColors || []).filter(c => HEX_PATTERN.test(c.hex_color));
  return {
    // Self-hosted (npm, not TinyMCE Cloud) — agrees to the open source
    // license terms. skin/content_css are false because the CSS is
    // imported directly by RichHtmlEditor.jsx rather than fetched from a URL.
    license_key: 'gpl',
    skin: false,
    content_css: false,
    height,
    menubar: false,
    // TinyMCE's default `relative_urls: true` rewrites any inserted
    // absolute URL that shares this app's own domain into a
    // document-relative one at save time (e.g. our "Marka Kütüphanesinden
    // Ekle" picker's real https://imza.martinci.com/api/signatures/
    // brand-assets/<id>/raw/ became the bare string
    // "api/signatures/brand-assets/4/raw/", no scheme/host, not even a
    // leading slash) — resolves back correctly only on the exact editor
    // page it was authored on, so it looked fine in the admin panel but
    // was a dead link everywhere else: the sent email, the taskpane
    // preview, TinyMCE's own native "Insert Image" dialog when it's used
    // to pick a brand asset. Found 2026-09-23 from a real broken-image
    // report; confirmed by curling the live outlook-addin/user-signature
    // endpoint and seeing the mangled relative src verbatim. Both options
    // are needed — remove_script_host alone still strips the domain even
    // with relative_urls off.
    relative_urls: false,
    remove_script_host: false,
    convert_urls: false,
    ...(validBrandColors.length > 0 ? {
      color_map: [
        ...validBrandColors.flatMap(c => [c.hex_color.replace('#', '').toUpperCase(), c.name]),
        ...curatedColorMap(),
      ],
    } : {}),
    // Matches the richer plugin/toolbar set used by the TinyMCE editor in
    // the TopluMail (bulk mail) product this was compared against — this
    // one used a much sparser curated toolbar and looked inconsistent with
    // it. statusbar left at its default (true) since the wordcount plugin's
    // counter lives there. "help" is deliberately excluded — self-hosted
    // (npm-imported, not TinyMCE Cloud) that plugin tries to fetch an extra
    // i18n resource file over HTTP that isn't part of the bundle, which
    // throws a real runtime error rather than silently degrading.
    plugins: 'advlist autolink lists link image charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime media table wordcount',
    toolbar: 'undo redo | blocks | bold italic underline strikethrough | fontfamily fontsize forecolor backcolor | alignleft aligncenter alignright alignjustify | link image table | bullist numlist outdent indent | removeformat hideifempty | code fullscreen',
    // "Boşsa Gizle": wraps the current selection (expected to contain one
    // {{Token}} placeholder, typically with a separator/icon around it, e.g.
    // " | {{Mobile}}") in <span data-hide-if-empty="Token">...</span>. The
    // backend (graph_utils.render_signature_html) strips that whole span at
    // render time when the token's directory value came back empty —
    // without this, an empty field still leaves its separator visibly
    // stranded (plain {{Token}} substitution only clears the placeholder
    // text itself). Auto-detects which token from the selection's own
    // content rather than asking the admin to type/pick a field name again.
    setup: (editor) => {
      editor.ui.registry.addButton('hideifempty', {
        text: pick(HIDE_IF_EMPTY.text),
        tooltip: pick(HIDE_IF_EMPTY.tooltip),
        onAction: () => {
          const selected = editor.selection.getContent({ format: 'html' });
          const match = selected.match(/\{\{(\w+)\}\}/);
          if (!match) {
            editor.notificationManager.open({
              text: pick(HIDE_IF_EMPTY.warning),
              type: 'warning',
              timeout: 4000,
            });
            return;
          }
          editor.selection.setContent(`<span data-hide-if-empty="${match[1]}">${selected}</span>`);
        },
      });
    },
    // Pasted/dropped images (e.g. a signature copied out of Outlook/Word)
    // arrive as base64 data: URIs. Those look fine here but Outlook on the
    // web drops them from a reply's quoted thread, so every earlier mail's
    // signature logo vanished (found 2026-09-26). Upload them to the org's
    // Brand Asset library instead and embed the public https URL — the same
    // form the image_list picker below produces. The backend serializers
    // also convert any data: image that still gets through (see
    // signatures/inline_images.py), this just keeps the editor honest.
    automatic_uploads: true,
    images_upload_handler: (blobInfo) => {
      const formData = new FormData();
      formData.append('file', blobInfo.blob(), blobInfo.filename());
      formData.append('name', blobInfo.filename());
      return api
        .post(endpoints.signatures.brandAssets.list, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(asset => asset.url);
    },
    // Also offer a pick from the org's Brand Asset library ("Marka
    // Varlıkları" page, /brand-assets) in the image dialog.
    image_list: (success) => {
      api.get(endpoints.signatures.brandAssets.list)
        .then(assets => success((assets || []).map(a => ({ title: a.name, value: a.url }))))
        .catch(() => success([]));
    },
    content_style: `body { font-family: Arial, Helvetica, sans-serif; font-size: 14px; color: #1a1a1a; } div { ${EMAIL_PARAGRAPH_STYLE} }`,
    // <p> tags specifically: once the user starts typing in the compose
    // body, Outlook's own editing surface re-applies its default paragraph
    // spacing to every <p> in the body — including the signature's, even
    // though each one had its own inline margin. This is why the signature
    // looked right immediately on insert but then visibly loosened up the
    // moment any text was typed. <div> isn't subject to that "Normal
    // style" paragraph reformatting, so it holds its own inline styling.
    // Pasting a signature copied from Outlook/Word/a web page: Chromium puts
    // every effective style (text color, font, table cell borders/padding)
    // inline on the copied nodes, and TinyMCE's default WebKit paste filter
    // strips ALL of them (paste_webkit_styles: 'none') — which turned a blue
    // Aptos signature with a vertical divider into plain black Arial text
    // with no divider. Keep them so a pasted signature looks like the source.
    paste_remove_styles_if_webkit: false,
    paste_webkit_styles: 'all',
    forced_root_block: 'div',
    forced_root_block_attrs: { style: EMAIL_PARAGRAPH_STYLE },
    // Curated rather than TinyMCE's default list, which includes joke/
    // decorative entries (Wingdings, Comic Sans MS, Impact) not appropriate
    // for a corporate signature tool. Three groups:
    //  - true web-safe fonts (installed on ~every OS, will render as picked
    //    everywhere)
    //  - Outlook/Microsoft 365's own UI fonts (Calibri was the Office
    //    default for years; Aptos replaced it in 2023+) — installed on
    //    Windows/Microsoft 365 machines, not guaranteed elsewhere
    //  - popular web fonts (Poppins, Roboto, etc.) — NOT installed by
    //    default on most systems (email clients don't support @font-face),
    //    so these fall back to the trailing generic in the stack for most
    //    recipients. Offered because they were asked for, not because
    //    they're guaranteed to render as chosen — same trade-off every
    //    signature tool makes with this font group.
    font_family_formats:
      'Arial=arial,helvetica,sans-serif;' +
      'Georgia=georgia,serif;' +
      'Tahoma=tahoma,arial,sans-serif;' +
      'Times New Roman=times new roman,times,serif;' +
      'Trebuchet MS=trebuchet ms,geneva,sans-serif;' +
      'Verdana=verdana,geneva,sans-serif;' +
      'Courier New=courier new,courier,monospace;' +
      'Helvetica=helvetica,arial,sans-serif;' +
      'Calibri=calibri,candara,segoe,segoe ui,optima,arial,sans-serif;' +
      'Aptos=aptos,calibri,segoe ui,arial,sans-serif;' +
      'Segoe UI=segoe ui,segoe,tahoma,arial,sans-serif;' +
      'Cambria=cambria,georgia,serif;' +
      'Poppins=poppins,arial,sans-serif;' +
      'Roboto=roboto,arial,sans-serif;' +
      'Open Sans=open sans,arial,sans-serif;' +
      'Lato=lato,arial,sans-serif;' +
      'Montserrat=montserrat,arial,sans-serif;',
    font_size_formats: '10px 12px 14px 16px 18px 20px 24px 28px',
    font_size_input_default_unit: 'px',
  };
}
